import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { PageLayout } from "../../components";
import {
  isNegativeReasonSubStatus,
  negativeReasonIdsBySubcase,
} from "../../constants/negativeReasonParameterGuids";
import { isNegativeInterviewResult } from "../../constants/interviewResultParameterGuids";
import {
  isSubStatusIn,
  subStatusExtendedForm,
} from "../../constants/subStatusParameterGuids";
import { ThemeContext } from "../../context/ThemeContextProvider";
import { Box, CircularProgress } from "@mui/material";
import { AIRecommendedPositionsCard } from "./components/cards/AIRecommendedPositionsCard";
import { PositionDetailCard } from "./components/cards/PositionDetailCard";
import { QuestionListCard } from "./components/cards/QuestionListCard";
import { AIQuestionListCard } from "./components/cards/AIQuestionListCard";
import {
  CandidateHeader,
  InterviewResultOption,
  NegativeReasonOption,
} from "./components/CandidateHeader";
import { HrCommentCard } from "./components/cards/HrCommentCard";
import { CandidateDetailsCard } from "../../components/shared/CandidateDetailCard/CandidateDetailsCard";
import axios from "axios";
import { SERVICEURL, WEBSOCKET_URL } from "../../appconfig";
import { useParams } from "react-router-dom";
import { useToast } from "../../shared/hooks/useToast";
import {
  useAiSuggestedPositions,
  type AiPosition,
} from "../../hooks/candidate/useAiSuggestedPositions";
import PositionDetailModal from "./modals/PositionDetailModal";
import AddPositionModal from "./modals/AddPositionModal";
import PositionDescriptionModal from "./modals/PositionDescriptionModal";
import ScreenRecorder from "../screenRecorder/ScreenRecorder";
import { hrQuestionsData } from "../../shared/data";
import {
  pageLoadingSx,
  threeColumnLayoutSx,
  pageColumnSx,
  pageColumnWideSx,
  cardFlexAutoSx,
  cardFlex1Sx,
  cardFlex2Sx,
  cardFlex3Sx,
} from "./style";

type ResponseObjectDTO<T> = {
  code: number;
  message: string;
  data: T;
};

type GetMemberDetailsDTO = {
  name: string;
  surname: string;
  memberId: string;
  talentId?: string | null;
  interviewAiId?: string | null;
  userName: string; // recruiter first
  userSurname: string; // recruiter last
  // aiServiceTalentUrl / aiServiceInterviewsUrl kaldirildi (task 11710): ICS'in ic adresi
  // artik istemciye dagitilmiyor, talent/interview olusturma BE uclari uzerinden yapiliyor.
};

const hrQuestionsDefault: QuestionItem[] = hrQuestionsData.map((q) => ({
  id: q.id,
  question: q.question,
  checked: false,
}));

type CandidateInfo = {
  id: string;
  name: string;
  surname: string;
  techGroups: string[];
  subStatus: string;
  subStatusId: string | null;
  interviewDate: string;
  kvkkStatus: string;
  interviewResultId: string | null;
  negativeReasonId: string | null;
  /** 0-100 manuel değerlendirme skoru (upstream 942593f). Kimlik değil → number. */
  manuelScore: number | null;
  hrComment: string;
  positionId?: string;
};

export type PositionInfo = {
  id: string;
  companyId: string | number;
  companyName: string;
  clientId: string | number;
  clientName: string;
  teamId: string | number;
  teamName: string | null;
  positionParameterId: string | number;
  positionParameterName: string;
  // Bu uc koleksiyon API'den NULL gelebilir; daha once non-nullable dizi olarak
  // tanimlanmislardi ve derleyici korumasiz `.map()` cagrilarini yakalamiyordu.
  // Lokasyonu olmayan bir pozisyon (Remote-only) mulakat asistani ekranini
  // komple dusuruyordu. Tip artik gerceklige uyuyor.
  techGroup?: Array<{
    id: string | number;
    techGroupId: string | number;
    techGroupName: string;
  }> | null;
  headCount: number;
  workingModelId: string | number;
  workingModelName: string;
  location?: Array<{
    id?: string | number;
    locationId?: string | number;
    locationName?: string;
    cityId?: string | number;
    cityName?: string;
    townIds?: Array<string | number>;
    towns?: Array<{
      townId: string | number;
      townName: string;
    }>;
  }> | null;
  priorityId: string | number;
  priorityName: string | null;
  statusId: string | number;
  statusName: string;
  responsibleMembers?: Array<{
    id: string | number;
    memberId: string | number;
    name: string;
  }> | null;
  description: string | null;
  projectKindName: string | null;
  minExperienceYear: number | null;
  detailDescription: string | null;
  criticalCriteria?: string[];
  mustCriteria?: string[];
  optionalCriteria?: string[];
  diffPoint: number | null;
  publishDescription: string | null;
  deadline: string | null;
  isPublished: boolean;
  createdBy: string;
  createdAt: string;
};

export type AIPositionInfo = {
  positionId: string;
  positionDetails: string;
  justification: string;
  explanation: string;
  aiExplanation?: string;
  matchScore: number;
  techGroupNames: string[];
  workingModel: string | null;
  client: string | null;
  team: string | null;
};

export type QuestionItem = {
  id: number;
  question: string;
  checked: boolean;
};

type State = {
  loadingInterviewDetails: boolean;
  loadingAiEvaluatedPosition: boolean;
  loadingAiRecommendedPositions: boolean;
  loadingAiQuestionList: boolean;
  loadingInterviewResultOptions: boolean;
  loadingNegativeReasonOptions: boolean;
  candidateInfo: CandidateInfo | null;
  positionInfo: PositionInfo | null;
  aiEvaluatedPosition: AIPositionInfo | null;
  aiRecommendedPositions: AIPositionInfo[] | null;
  aiHrQuestions: QuestionItem[] | null;
  aiTechnicalQuestions: QuestionItem[] | null;
  hrQuestions: QuestionItem[] | null;
  interviewResultOptions: InterviewResultOption[];
  negativeReasonOptions: NegativeReasonOption[];
};

const initialState: State = {
  loadingInterviewDetails: false,
  loadingAiEvaluatedPosition: false,
  loadingAiRecommendedPositions: false,
  loadingAiQuestionList: false,
  loadingInterviewResultOptions: false,
  loadingNegativeReasonOptions: false,
  candidateInfo: null,
  positionInfo: null,
  aiEvaluatedPosition: null,
  aiRecommendedPositions: null,
  aiHrQuestions: null,
  aiTechnicalQuestions: null,
  hrQuestions: hrQuestionsDefault,
  interviewResultOptions: [],
  negativeReasonOptions: [],
};

type Action =
  | { type: "setLoadingInterviewDetails"; payload: boolean }
  | { type: "setLoadingAiEvaluatedPosition"; payload: boolean }
  | { type: "setLoadingAiRecommendedPositions"; payload: boolean }
  | { type: "setLoadingAiQuestionList"; payload: boolean }
  | { type: "setLoadingInterviewResultOptions"; payload: boolean }
  | { type: "setLoadingNegativeReasonOptions"; payload: boolean }
  | { type: "setCandidateInfo"; payload: CandidateInfo | null }
  | { type: "patchCandidateInfo"; payload: Partial<CandidateInfo> }
  | { type: "setPositionInfo"; payload: PositionInfo | null }
  | { type: "setAiEvaluatedPosition"; payload: AIPositionInfo | null }
  | { type: "setAiRecommendedPositions"; payload: AIPositionInfo[] | null }
  | { type: "setAiHrQuestions"; payload: QuestionItem[] | null }
  | { type: "setAiTechnicalQuestions"; payload: QuestionItem[] | null }
  | { type: "setHrQuestions"; payload: QuestionItem[] | null }
  | { type: "setInterviewResultOptions"; payload: InterviewResultOption[] }
  | { type: "setNegativeReasonOptions"; payload: NegativeReasonOption[] };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "setLoadingInterviewDetails":
      return { ...state, loadingInterviewDetails: action.payload };
    case "setLoadingAiEvaluatedPosition":
      return { ...state, loadingAiEvaluatedPosition: action.payload };
    case "setLoadingAiRecommendedPositions":
      return { ...state, loadingAiRecommendedPositions: action.payload };
    case "setLoadingAiQuestionList":
      return { ...state, loadingAiQuestionList: action.payload };
    case "setCandidateInfo":
      return { ...state, candidateInfo: action.payload };
    // Alan bazli guncelleme. setCandidateInfo ile {...state.candidateInfo, alan}
    // gondermek, cagiran fonksiyonun kendi render'indaki BAYAT nesneyi yayar; iki
    // guncelleme es zamanli calistiginda son dispatch digerinin alanini eski degere
    // dondururdu (mulakat sonucu <-> olumsuz neden). Reducer guncel state'i gordugu
    // icin burada boyle bir yaris yok.
    case "patchCandidateInfo":
      return state.candidateInfo
        ? {
            ...state,
            candidateInfo: { ...state.candidateInfo, ...action.payload },
          }
        : state;
    case "setPositionInfo":
      return { ...state, positionInfo: action.payload };
    case "setAiEvaluatedPosition":
      return { ...state, aiEvaluatedPosition: action.payload };
    case "setAiRecommendedPositions":
      return { ...state, aiRecommendedPositions: action.payload };
    case "setAiHrQuestions":
      return { ...state, aiHrQuestions: action.payload };
    case "setAiTechnicalQuestions":
      return { ...state, aiTechnicalQuestions: action.payload };
    case "setHrQuestions":
      return { ...state, hrQuestions: action.payload };
    case "setLoadingInterviewResultOptions":
      return { ...state, loadingInterviewResultOptions: action.payload };
    case "setLoadingNegativeReasonOptions":
      return { ...state, loadingNegativeReasonOptions: action.payload };
    case "setInterviewResultOptions":
      return { ...state, interviewResultOptions: action.payload };
    case "setNegativeReasonOptions":
      return { ...state, negativeReasonOptions: action.payload };
    default:
      return state;
  }
};

const InterviewAssistant: React.FC = () => {
  const { darkModeActive } = useContext(ThemeContext);
  const { stateId: stateIdParam } = useParams<{ stateId: string }>();
  const stateIdFromUrl = stateIdParam?.trim() || null;

  const [state, dispatch] = useReducer(reducer, initialState);
  const { showToast } = useToast();
  const mytoken = localStorage.getItem("token");

  const [flowLoading, setFlowLoading] = useState(false);
  const [positionDetailModalOpen, setPositionDetailModalOpen] = useState(false);
  const [positionDescriptionModalOpen, setPositionDescriptionModalOpen] =
    useState(false);
  const [addPositionModalOpen, setAddPositionModalOpen] = useState(false);
  const [modalPositionId, setModalPositionId] = useState<string | null>(null);
  const [recordingPopupOpen, setRecordingPopupOpen] = useState(false);
  const [currentInterviewId, setCurrentInterviewId] = useState<string | null>(
    null,
  );
  const [talentId, setTalentId] = useState<string | undefined>(undefined);
  const [aiHookEnabled, setAiHookEnabled] = useState(false);
  const [savedPositions, setSavedPositions] = useState<AIPositionInfo[] | null>(
    null,
  );
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [positionsSourceReady, setPositionsSourceReady] = useState(false);

  const {
    positions: hookPositions,
    loading: hookLoading,
    resetCache: resetAiCache,
  } = useAiSuggestedPositions(talentId, aiHookEnabled);

  const mapHookPositionToInfo = (p: AiPosition): AIPositionInfo => ({
    positionId: String(p.positionId),
    positionDetails: "",
    justification: p.justification || "",
    explanation: p.explanation || "",
    matchScore: p.matchScore,
    techGroupNames: p.techGroupNames,
    workingModel: p.workingModel || null,
    client: p.client || null,
    team: p.team || null,
  });

  const hookMappedPositions = useMemo(
    () => hookPositions.map(mapHookPositionToInfo),
    [hookPositions],
  );

  const positions = savedPositions ?? hookMappedPositions;
  const positionsLoading =
    !positionsSourceReady ||
    loadingSaved ||
    (savedPositions === null && aiHookEnabled && hookLoading);

  useEffect(() => {
    dispatch({
      type: "setLoadingAiRecommendedPositions",
      payload: positionsLoading,
    });
  }, [positionsLoading]);

  useEffect(() => {
    if (savedPositions !== null || hookLoading || !aiHookEnabled) return;

    dispatch({
      type: "setAiRecommendedPositions",
      payload: hookMappedPositions.length > 0 ? hookMappedPositions : null,
    });
  }, [
    savedPositions,
    hookLoading,
    aiHookEnabled,
    hookMappedPositions,
  ]);

  const interviewedPositionMatch = useMemo((): AIPositionInfo | null => {
    if (positionsLoading) return null;

    const positionId =
      state.positionInfo?.id != null ? String(state.positionInfo.id) : null;
    if (!positionId) return null;

    return positions.find((p) => p.positionId === positionId) ?? null;
  }, [positionsLoading, state.positionInfo?.id, positions]);

  const getInterviewDetails = async (stateId: string) => {
    dispatch({ type: "setLoadingInterviewDetails", payload: true });

    try {
      const response = await axios.get(
        `${SERVICEURL}api/StateInfo/${stateId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;
      const responseData = response.data.data;

      if (code !== 200 || !responseData) {
        showToast(message, "error");
        dispatch({ type: "setCandidateInfo", payload: null });
        return;
      }

      const candidateInfo: CandidateInfo = {
        id: responseData.memberId != null ? String(responseData.memberId) : "",
        name: responseData.memberName || "",
        surname: responseData.memberSurname || "",
        techGroups: responseData.personalTechGroup || [],
        subStatus: responseData.subStatusName || "",
        subStatusId: responseData.subStatusId ?? null,
        interviewDate: responseData.interviewDate || "",
        kvkkStatus: responseData.kvkkStatusName || "",
        interviewResultId: responseData.interviewResultId || null,
        negativeReasonId: responseData.negativeReasonId || null,
        // StateInfoDetailDTO.ManuelScore BE'de int? ve detay sorgusunda donuyor.
        manuelScore:
          responseData.manuelScore != null ? responseData.manuelScore : null,
        hrComment: responseData.statement || "",
        positionId:
          responseData.positionId != null
            ? String(responseData.positionId)
            : undefined,
      };

      dispatch({
        type: "setCandidateInfo",
        payload: candidateInfo,
      });

      if (responseData?.positionId) {
        getPositionDetail(String(responseData.positionId));
      }
      return candidateInfo.id || null;
    } catch (error: any) {
      showToast(
        error.response?.data?.message ||
          "Görüşme detayları yüklenirken hata oluştu",
        "error",
      );
      dispatch({ type: "setCandidateInfo", payload: null });
      return null;
    } finally {
      dispatch({ type: "setLoadingInterviewDetails", payload: false });
    }
  };

  const getPositionDetail = async (positionId: string) => {
    try {
      const response = await axios.get(
        `${SERVICEURL}api/Position/GetPositionDetail?id=${positionId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;
      const responseData = response.data.data;

      if (code !== 200 || !responseData) {
        showToast(message, "error");
        dispatch({ type: "setPositionInfo", payload: null });
        return;
      }

      dispatch({
        type: "setPositionInfo",
        payload: {
          ...(responseData as PositionInfo),
          id: String(responseData.id),
        },
      });
    } catch (error: any) {
      showToast(
        error.response?.data?.message ||
          "Pozisyon detayları yüklenirken hata oluştu.",
        "error",
      );
      dispatch({ type: "setPositionInfo", payload: null });
    }
  };

  const getSavedRecommendedPositions = async (
    memberId: string,
  ): Promise<AIPositionInfo[]> => {
    try {
      const response = await axios.get(
        `${SERVICEURL}api/StateInfo/member/${memberId}/SuggestedPositions`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const responseData = response.data.data;

      if (code !== 200 || !responseData || responseData.length === 0) {
        return [];
      }

      return responseData.map((item: any) => ({
        positionId: String(item.positionId),
        positionDetails: item.positionDetails || "",
        justification: item.aiJustification || "",
        explanation: item.aiExplanation || "",
        aiExplanation: item.aiExplanation || "",
        matchScore: item.aiMatchScore || 0,
        techGroupNames: item.techGroupNames || [],
        workingModel: item.workingModel || null,
        client: item.client || null,
        team: item.team || null,
      })) as AIPositionInfo[];
    } catch {
      return [];
    }
  };

  const getAiEvaluatedPosition = async (stateId: string) => {
    dispatch({ type: "setLoadingAiEvaluatedPosition", payload: true });

    try {
      const response = await axios.post(
        `${SERVICEURL}api/InterviewAssistant/EvaluateInterviewedPosition?stateId=${stateId}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;
      const responseData = response.data.data;
      console.log(code, message);

      if (code !== 200) {
        // showToast(message, "error");
        dispatch({ type: "setAiEvaluatedPosition", payload: null });
        return;
      }
      dispatch({
        type: "setAiEvaluatedPosition",
        payload: responseData as AIPositionInfo,
      });
    } catch (error: any) {
      // showToast(
      //   error.response?.data?.message ||
      //     "Pozisyon değerlendirmesi yüklenirken hata oluştu.",
      //   "error",
      // );
      dispatch({ type: "setAiEvaluatedPosition", payload: null });
    } finally {
      dispatch({ type: "setLoadingAiEvaluatedPosition", payload: false });
    }
  };

  const getAiInterviewQuestions = async (stateId: string) => {
    dispatch({ type: "setLoadingAiQuestionList", payload: true });

    const requestData = {
      stateId: stateId,
      existingQuestions:
        state.hrQuestions?.map((question) => question.question) || [],
    };

    try {
      const response = await axios.post(
        `${SERVICEURL}api/InterviewAssistant/GenerateQuestions`,
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;
      const responseData = response.data.data;
      const hrQuestions = responseData?.hrQuestions;
      const technicalQuestions = responseData?.technicalQuestions;

      if (code !== 200) {
        // showToast(message, "error");
        dispatch({ type: "setAiHrQuestions", payload: null });
        dispatch({ type: "setAiTechnicalQuestions", payload: null });
        return;
      }

      const formattedHrQuestions = (hrQuestions || []).map(
        (question: string, index: number) => ({
          id: index + 1,
          question: question,
          checked: false,
        }),
      );
      const formattedTechnicalQuestions = (technicalQuestions || []).map(
        (question: string, index: number) => ({
          id: index + 1,
          question: question,
          checked: false,
        }),
      );
      dispatch({
        type: "setAiHrQuestions",
        payload: formattedHrQuestions as QuestionItem[],
      });
      dispatch({
        type: "setAiTechnicalQuestions",
        payload: formattedTechnicalQuestions as QuestionItem[],
      });
    } catch (error: any) {
      // showToast(
      //   error.response?.data?.message ||
      //     "Görüşme soruları yüklenirken hata oluştu.",
      //   "error",
      // );
      dispatch({ type: "setAiHrQuestions", payload: null });
      dispatch({ type: "setAiTechnicalQuestions", payload: null });
    } finally {
      dispatch({ type: "setLoadingAiQuestionList", payload: false });
    }
  };

  const getInterviewResultList = async () => {
    dispatch({ type: "setLoadingInterviewResultOptions", payload: true });

    try {
      const response = await axios.get(
        `${SERVICEURL}api/Data/GetInterviewResultList`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;
      const responseData = response.data.data;

      if (code !== 200 || !responseData) {
        showToast(message, "error");
        dispatch({ type: "setInterviewResultOptions", payload: [] });
        return;
      }

      const formattedOptions: InterviewResultOption[] = responseData.map(
        (item: { id: string; name: string }) => ({
          value: item.id,
          label: item.name,
        }),
      );

      dispatch({
        type: "setInterviewResultOptions",
        payload: formattedOptions,
      });
    } catch (error: any) {
      showToast(
        error.response?.data?.message ||
          "Mülakat sonuçları yüklenirken hata oluştu.",
        "error",
      );
      dispatch({ type: "setInterviewResultOptions", payload: [] });
    } finally {
      dispatch({ type: "setLoadingInterviewResultOptions", payload: false });
    }
  };

  const getNegativeReasonList = async () => {
    dispatch({ type: "setLoadingNegativeReasonOptions", payload: true });

    try {
      const response = await axios.get(
        `${SERVICEURL}api/Data/GetNegativeReasonsList`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;
      const responseData = response.data.data;

      if (code !== 200 || !responseData) {
        showToast(
          message || "Olumsuz nedenleri yüklenirken hata oluştu.",
          "error",
        );
        dispatch({ type: "setNegativeReasonOptions", payload: [] });
        return;
      }

      const formattedOptions: NegativeReasonOption[] = responseData.map(
        (item: { id: string; name: string }) => ({
          value: item.id,
          label: item.name,
        }),
      );

      dispatch({
        type: "setNegativeReasonOptions",
        payload: formattedOptions,
      });
    } catch (error: any) {
      showToast(
        error.response?.data?.message ||
          "Olumsuz nedenleri yüklenirken hata oluştu.",
        "error",
      );
      dispatch({ type: "setNegativeReasonOptions", payload: [] });
    } finally {
      dispatch({ type: "setLoadingNegativeReasonOptions", payload: false });
    }
  };

  const updateStatement = async (statement: string) => {
    if (!stateIdFromUrl) {
      showToast("State ID bulunamadı.", "error");
      return;
    }

    try {
      const response = await axios.patch<
        ResponseObjectDTO<{ statement: string }>
      >(
        `${SERVICEURL}api/StateInfo/${stateIdFromUrl}/Statement`,
        { Statement: statement },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;
      const data = response.data.data;

      if (code !== 200) {
        showToast(
          message || "Görüşme notu güncellenirken hata oluştu",
          "error",
        );
        return;
      }

      if (state.candidateInfo && data) {
        const updatedCandidateInfo = {
          ...state.candidateInfo,
          hrComment: data.statement,
        };
        dispatch({
          type: "setCandidateInfo",
          payload: updatedCandidateInfo,
        });
      }

      showToast(message || "Görüşme notu başarıyla kaydedildi.", "success");
    } catch (error: any) {
      showToast(
        error.response?.data?.message ||
          "Görüşme notu güncellenirken hata oluştu.",
        "error",
      );
    }
  };

  const updateInterviewResult = async (interviewResultId: string | null) => {
    if (!stateIdFromUrl) {
      showToast("State ID bulunamadı.", "error");
      return;
    }

    // Upstream 5a8f50f: İK Görüşme 1-2, Müş. Görüşme 1-4 ve Teklif'te mülakat
    // sonucu boşaltılamaz. Bu küme subStatusExtendedForm ile birebir aynı
    // (upstream ham liste `[1, 2, 5, 6, 7, 32, 13]`).
    if (
      interviewResultId == null &&
      isSubStatusIn(state.candidateInfo?.subStatusId, subStatusExtendedForm)
    ) {
      showToast("Lütfen mülakat sonucu seçiniz", "error");
      return;
    }

    try {
      const response = await axios.patch<
        ResponseObjectDTO<{ interviewResultId: string | null }>
      >(
        `${SERVICEURL}api/StateInfo/${stateIdFromUrl}/InterviewResult`,
        { InterviewResultId: interviewResultId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;
      const data = response.data.data;

      if (code !== 200) {
        showToast(
          message || "Mülakat sonucu güncellenirken hata oluştu",
          "error",
        );
        return;
      }

      if (data) {
        dispatch({
          type: "patchCandidateInfo",
          payload: { interviewResultId: data.interviewResultId },
        });
      }

      showToast(message || "Mülakat sonucu başarıyla güncellendi.", "success");
    } catch (error: any) {
      showToast(
        error.response?.data?.message ||
          "Mülakat sonucu güncellenirken hata oluştu.",
        "error",
      );
    }
  };

  const updateNegativeReason = async (
    negativeReasonId: string | null,
    options?: { silent?: boolean },
  ) => {
    if (!stateIdFromUrl) {
      showToast("State ID bulunamadı.", "error");
      return;
    }

    // Upstream 5a8f50f: mülakat sonucu "Olumsuz" VE alanın gösterildiği bir alt
    // durumsa olumsuz neden zorunlu. Upstream `interviewResultId === 2` sayı
    // karşılaştırması yapıyor; Guid'de hiçbir zaman tutmaz (tuzak #1/#2), o
    // yüzden isNegativeInterviewResult (Guid + etiket) kullanılıyor.
    const selectedResult = state.interviewResultOptions.find(
      (option) => option.value === state.candidateInfo?.interviewResultId,
    );
    const isNegativeReasonRequired =
      isNegativeInterviewResult(
        state.candidateInfo?.interviewResultId,
        selectedResult?.label,
      ) && isNegativeReasonSubStatus(state.candidateInfo?.subStatusId);

    if (
      negativeReasonId == null &&
      isNegativeReasonRequired &&
      !options?.silent
    ) {
      showToast("Lütfen olumsuz nedenini seçiniz", "error");
      return;
    }

    try {
      const response = await axios.patch<
        ResponseObjectDTO<{ negativeReasonId: string | null }>
      >(
        `${SERVICEURL}api/StateInfo/${stateIdFromUrl}/NegativeReason`,
        { NegativeReasonId: negativeReasonId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;
      const data = response.data.data;

      if (code !== 200) {
        if (!options?.silent) {
          showToast(
            message || "Olumsuz nedeni güncellenirken hata oluştu",
            "error",
          );
        }
        return;
      }

      dispatch({
        type: "patchCandidateInfo",
        payload: {
          negativeReasonId:
            data?.negativeReasonId !== undefined
              ? data.negativeReasonId
              : negativeReasonId,
        },
      });

      if (!options?.silent) {
        showToast(
          message || "Olumsuz nedeni başarıyla güncellendi.",
          "success",
        );
      }
    } catch (error: any) {
      if (!options?.silent) {
        showToast(
          error.response?.data?.message ||
            "Olumsuz nedeni güncellenirken hata oluştu.",
          "error",
        );
      }
    }
  };

  /**
   * Manuel skoru anında kaydeder (upstream 942593f, board #11753).
   * BE ucu: PATCH api/StateInfo/{stateId}/ManuelScore.
   */
  const updateManuelScore = async (manuelScore: number | null) => {
    if (!stateIdFromUrl) {
      showToast("State ID bulunamadı.", "error");
      return;
    }

    const selectedResult = state.interviewResultOptions.find(
      (option) => option.value === state.candidateInfo?.interviewResultId,
    );
    const isOlumsuz = isNegativeInterviewResult(
      state.candidateInfo?.interviewResultId,
      selectedResult?.label,
    );

    if (isOlumsuz) {
      if (
        manuelScore == null ||
        Number.isNaN(manuelScore) ||
        manuelScore < 0 ||
        manuelScore > 100
      ) {
        showToast("Lütfen 0-100 arası bir değer giriniz", "error");
        return;
      }
    }

    try {
      const response = await axios.patch<
        ResponseObjectDTO<{ manuelScore: number | null }>
      >(
        `${SERVICEURL}api/StateInfo/${stateIdFromUrl}/ManuelScore`,
        { ManuelScore: manuelScore },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;
      const data = response.data.data;

      if (code !== 200) {
        showToast(message || "Manuel skor güncellenirken hata oluştu", "error");
        return;
      }

      // patchCandidateInfo: ALAN BAZLI birleştirme. Upstream burada tüm
      // candidateInfo nesnesini setCandidateInfo ile yayıyor; mülakat sonucu ve
      // olumsuz neden uçları da aynı anda tetiklendiği için son dispatch
      // diğerinin alanını eski değere döndürürdü (playbook tuzak #14).
      dispatch({
        type: "patchCandidateInfo",
        payload: {
          manuelScore:
            data?.manuelScore !== undefined ? data.manuelScore : manuelScore,
        },
      });

      showToast(message || "Manuel skor başarıyla güncellendi.", "success");
    } catch (error: any) {
      showToast(
        error.response?.data?.message ||
          "Manuel skor güncellenirken hata oluştu.",
        "error",
      );
    }
  };

  useEffect(() => {
    getInterviewResultList();
    getNegativeReasonList();
  }, []);

  useEffect(() => {
    const initPageData = async () => {
      if (stateIdFromUrl) {
        const memberId = await getInterviewDetails(stateIdFromUrl);

        if (memberId) {
          try {
            const det = await getMemberDetailsForAi(memberId, stateIdFromUrl);

            // 3) Talent yoksa BE uzerinden oluştur; BE aynı istekte Wiser'a da yazar (task 11710)
            let effectiveTalentId = det.talentId ?? null;
            if (!effectiveTalentId) {
              effectiveTalentId = await createTalent(det.memberId);
            }
            setTalentId(effectiveTalentId ?? undefined);

            // DB'de kayıtlı pozisyon var mı kontrol et
            setLoadingSaved(true);
            try {
              const savedFromDb = await getSavedRecommendedPositions(
                det.memberId,
              );
              if (savedFromDb.length > 0) {
                setSavedPositions(savedFromDb);
                dispatch({
                  type: "setAiRecommendedPositions",
                  payload: savedFromDb,
                });
              } else {
                setSavedPositions(null);
                setAiHookEnabled(true);
              }
            } finally {
              setLoadingSaved(false);
              setPositionsSourceReady(true);
            }
          } catch (error) {
            console.error("Talent check failed:", error);
            setPositionsSourceReady(true);
          }

          getAiInterviewQuestions(stateIdFromUrl);
          // getAiEvaluatedPosition(stateIdFromUrl);
        } else {
          setPositionsSourceReady(true);
        }
      }
    };

    initPageData();
  }, [stateIdFromUrl]);

  // /** ======== (YENİ) Recorder başlatıcı ======== */
  // const launchRecorder = (interviewId: string) => {
  //   const url = `screenrecorder://start?interviewId=${encodeURIComponent(
  //     interviewId
  //   )}`;
  //   const t0 = Date.now();
  //   window.location.href = url;
  //   setTimeout(() => {
  //     if (Date.now() - t0 < 1600) {
  //       showToast(
  //         "Kayıt uygulaması açılmadıysa kurulu olduğundan emin olun.",
  //         "warning"
  //       );
  //     }
  //   }, 1500);
  // };

  const getMemberDetailsForAi = async (memberId: string, stateId: string) => {
    const { data } = await axios.get<ResponseObjectDTO<GetMemberDetailsDTO>>(
      `${SERVICEURL}api/TranscriptAndSummary/GetMemberDetailsForAi`,
      {
        params: { memberId, stateId },
        headers: { Authorization: `Bearer ${mytoken}` },
      },
    );
    if (data.code !== 200 || !data.data)
      throw new Error(data.message || "GetMemberDetailsForAi failed");
    return {
      ...data.data,
      memberId: String(data.data.memberId),
    };
  };

  // Talent/interview olusturma artik BE uzerinden (task 11710). Onceden bu iki fonksiyon
  // dogrudan ICS'e gidiyordu; ICS'te CORS middleware olmadigi icin tarayicidan hic
  // calismiyordu (preflight OPTIONS -> 405, blocker 11700). BE ayrica ICS cagrisi ile
  // Wiser'a yazimi tek istekte yaptigi icin yetim talent olusma yarisi da kalkti.
  const createTalent = async (memberId: string) => {
    const { data } = await axios.post<ResponseObjectDTO<{ talentId: string }>>(
      `${SERVICEURL}api/TranscriptAndSummary/CreateTalent`,
      { memberId },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${mytoken}`,
        },
      },
    );
    const id = data?.data?.talentId;
    if (!id) throw new Error(data?.message || "CreateTalent: talentId alinamadi");
    return id;
  };

  const createInterview = async (memberId: string, stateId: string) => {
    const { data } = await axios.post<
      ResponseObjectDTO<{ talentId: string; interviewAiId: string }>
    >(
      `${SERVICEURL}api/TranscriptAndSummary/CreateInterview`,
      { memberId, stateId },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${mytoken}`,
        },
      },
    );
    const id = data?.data?.interviewAiId;
    if (!id)
      throw new Error(data?.message || "CreateInterview: interviewAiId alinamadi");
    return id;
  };



  const startInterviewFlow = async () => {
    if (flowLoading) return;
    if (!mytoken) {
      showToast("Oturum bulunamadı. Lütfen tekrar giriş yapın.", "error");
      return;
    }

    // TODO: Gerçek memberId ile bağla; şimdilik mevcut davranışı bozmadım
    const memberId = state.candidateInfo?.id || null;
    const stateId = stateIdFromUrl || null;

    if (!memberId || !stateId) {
      showToast("Member ID ve State ID bulunamadı.", "error");
      return;
    }

    try {
      setFlowLoading(true);
      // showToast("Screen Recorder başlatılıyor.", "success");

      // 1) Detayları çek (dinamik URL'ler burada)
      const det = await getMemberDetailsForAi(memberId, stateId);

      // 2) Mevcut interview varsa doğrudan recorder
      if (det.interviewAiId) {
        // showToast(
        //   `Mevcut interview bulundu: ${det.interviewAiId}`,
        //   "success"
        // );
        // 5) Popup'ı aç
        setCurrentInterviewId(det.interviewAiId);
        setRecordingPopupOpen(true);
        return;
      }

      // 3-4) Interview'i BE uzerinden oluştur (task 11710). BE talent yoksa onu da oluşturur,
      // her iki kimliği de aynı istekte Wiser'a yazar; ayrı UpdateTalentId/UpdateInterviewAiId
      // çağrılarına gerek kalmadı.
      const interviewId = await createInterview(det.memberId, stateId);

      // 5) Popup'ı aç
      setCurrentInterviewId(interviewId);
      setRecordingPopupOpen(true);
    } catch (err: any) {
      console.error(err);
      showToast(err?.message || "Görüşme başlatılırken hata oluştu", "error");
    } finally {
      setFlowLoading(false);
    }
  };

  /* ===================================================== */

  const handleRefreshPositions = () => {
    setSavedPositions(null);
    dispatch({ type: "setAiRecommendedPositions", payload: null });
    setAiHookEnabled(true);
    resetAiCache();
  };

  const handleDeleteAiRecommendedPosition = (positionId: string) => {
    dispatch({
      type: "setAiRecommendedPositions",
      payload:
        state.aiRecommendedPositions?.filter(
          (position) => position.positionId !== positionId,
        ) || null,
    });
  };

  const handlePositionClick = (positionId: string) => {
    setModalPositionId(positionId);
    setPositionDetailModalOpen(true);
  };

  const handleSaveAllRecommendedPositions = async () => {
    if (!stateIdFromUrl) {
      showToast("State ID bulunamadı.", "error");
      return;
    }

    try {
      const requestData = {
        items:
          state.aiRecommendedPositions?.map((position) => ({
            positionId: position.positionId,
            aiMatchScore: position.matchScore,
            aiJustification: position.justification || "",
            aiExplanation: position.explanation || "",
          })) || [],
      };

      const response = await axios.post(
        `${SERVICEURL}api/StateInfo/member/${state.candidateInfo?.id}/SuggestedPositions`,
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;

      if (code !== 200) {
        // showToast(message || "Pozisyonlar kaydedilirken hata oluştu.", "error");
        return;
      }

      showToast(message || "Tüm pozisyonlar başarıyla kaydedildi.", "success");
    } catch (error: any) {
      // showToast(
      //   error.response?.data?.message ||
      //     "Pozisyonlar kaydedilirken hata oluştu.",
      //   "error",
      // );
    }
  };

  const handleAddSelectedPositions = (selectedPositions: PositionInfo[]) => {
    // Seçilen pozisyonları AIPositionInfo formatına dönüştür ve listeye ekle
    const newAIPositions: AIPositionInfo[] = selectedPositions.map(
      (position) => ({
        positionId: String(position.id),
        positionDetails: "",
        justification: "",
        explanation: "",
        matchScore: 0,
        // `|| []` map'ten SONRA yazilmisti, yani hicbir sey korumuyordu: techGroup null
        // olsa `.map` zaten patlardi. Koruma artik map'ten once.
        techGroupNames: (position.techGroup ?? []).map((tg) => tg.techGroupName),
        workingModel: position.workingModelName || null,
        client: position.clientName || null,
        team: position.teamName || null,
      }),
    );

    // Mevcut listeye ekle (duplicate kontrolü yaparak)
    const existingIds =
      state.aiRecommendedPositions?.map((p) => p.positionId) || [];
    const uniqueNewPositions = newAIPositions.filter(
      (p) => !existingIds.includes(p.positionId),
    );

    if (uniqueNewPositions.length > 0) {
      dispatch({
        type: "setAiRecommendedPositions",
        payload: [
          ...(state.aiRecommendedPositions || []),
          ...uniqueNewPositions,
        ],
      });
      showToast(`${uniqueNewPositions.length} pozisyon eklendi.`, "success");
    } else {
      showToast("Seçilen pozisyonlar zaten listede mevcut.", "warning");
    }

    setAddPositionModalOpen(false);
  };

  const handleQuestionClick = (id: number) => {
    const question = state.hrQuestions?.find((question) => question.id === id);
    if (question) {
      dispatch({
        type: "setHrQuestions",
        payload:
          (state.hrQuestions?.map((q) =>
            q.id === id ? { ...q, checked: !q.checked } : q,
          ) as QuestionItem[]) || null,
      });
    }
  };

  const handleAiHrQuestionClick = (id: number) => {
    dispatch({
      type: "setAiHrQuestions",
      payload:
        (state.aiHrQuestions?.map((q) =>
          q.id === id ? { ...q, checked: !q.checked } : q,
        ) as QuestionItem[]) || null,
    });
  };

  const handleAiTechnicalQuestionClick = (id: number) => {
    dispatch({
      type: "setAiTechnicalQuestions",
      payload:
        (state.aiTechnicalQuestions?.map((q) =>
          q.id === id ? { ...q, checked: !q.checked } : q,
        ) as QuestionItem[]) || null,
    });
  };

  const isPageLoading =
    state.loadingInterviewDetails ||
    (stateIdFromUrl && !state.candidateInfo) ||
    (stateIdFromUrl &&
      state.loadingInterviewResultOptions &&
      state.interviewResultOptions.length === 0);

  // Upstream'de bu filtre dosya icinde FILTERED_NEGATIVE_REASON_IDS_BY_SUBSTATUS
  // (Record<number, number[]>) olarak duruyordu. Bizde ayni harita Guid formunda
  // constants/negativeReasonParameterGuids.ts icinde zaten var ve
  // NegativeCandidatesList de ayni kaynagi kullaniyor — kopyalamak yerine onu
  // paylasiyoruz. Haritada olmayan alt durumda filtre uygulanmaz (upstream ile ayni).
  const filteredNegativeReasonOptions = useMemo(() => {
    // Upstream 5a8f50f: haritada karşılığı OLMAYAN alt durumda liste BOŞ döner
    // (eskiden tüm nedenler dönüyordu — alan gösterilmemesi gereken yerde de
    // dolu bir liste sunuluyordu).
    const subStatusId = state.candidateInfo?.subStatusId;
    if (!subStatusId) return [];

    const allowedIds = negativeReasonIdsBySubcase[subStatusId];
    if (!allowedIds) return [];

    return state.negativeReasonOptions.filter((option) =>
      allowedIds.includes(option.value),
    );
  }, [state.candidateInfo?.subStatusId, state.negativeReasonOptions]);

  const showNegativeReason = isNegativeReasonSubStatus(
    state.candidateInfo?.subStatusId,
  );

  const interviewResultRequired = isSubStatusIn(
    state.candidateInfo?.subStatusId,
    subStatusExtendedForm,
  );

  return (
    <PageLayout
      title="Görüşme Asistanı"
      breadcrumbLinks={[
        {
          text: "Aday Profili",
          link: "/candidate-detail/" + state.candidateInfo?.id,
        },
        {
          text: "Görüşme Asistanı",
          link: "/interview-assistant/" + stateIdFromUrl,
        },
      ]}
      darkModeActive={darkModeActive}
    >
      {isPageLoading ? (
        <Box sx={pageLoadingSx}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <CandidateHeader
            fullName={
              state.candidateInfo?.name + " " + state.candidateInfo?.surname ||
              ""
            }
            techGroups={state.candidateInfo?.techGroups || []}
            subStatus={state.candidateInfo?.subStatus || ""}
            interviewDate={state.candidateInfo?.interviewDate || ""}
            kvkkStatus={state.candidateInfo?.kvkkStatus || ""}
            interviewResultId={state.candidateInfo?.interviewResultId ?? null}
            interviewResultOptions={state.interviewResultOptions}
            onInterviewResultChange={(interviewResultId) =>
              updateInterviewResult(interviewResultId)
            }
            interviewResultRequired={interviewResultRequired}
            negativeReasonId={state.candidateInfo?.negativeReasonId ?? null}
            negativeReasonOptions={filteredNegativeReasonOptions}
            showNegativeReason={showNegativeReason}
            onNegativeReasonChange={(negativeReasonId, options) =>
              updateNegativeReason(negativeReasonId, options)
            }
            manuelScore={state.candidateInfo?.manuelScore ?? null}
            onManuelScoreChange={(manuelScore) =>
              updateManuelScore(manuelScore)
            }
            onStartVideoCall={() => {
              if (!flowLoading) startInterviewFlow();
            }}
            recordingPopupOpen={recordingPopupOpen}
          />
          {/* Three Column Layout */}
          <Box sx={threeColumnLayoutSx}>
            {/* Left Column */}
            <Box sx={pageColumnSx}>
              <PositionDetailCard
                candidateName={state.candidateInfo?.name || ""}
                position={state.positionInfo}
                positionId={
                  state.positionInfo?.id != null
                    ? String(state.positionInfo.id)
                    : null
                }
                matchedPosition={interviewedPositionMatch}
                loading={positionsLoading}
                onPositionDetailClick={() => {
                  setModalPositionId(
                    state.positionInfo?.id != null
                      ? String(state.positionInfo.id)
                      : null,
                  );
                  setPositionDetailModalOpen(true);
                }}
                onPositionDescriptionClick={() => {
                  setPositionDescriptionModalOpen(true);
                }}
                sx={cardFlexAutoSx}
              />
              <AIRecommendedPositionsCard
                positions={state.aiRecommendedPositions}
                onAddNew={() => {
                  setAddPositionModalOpen(true);
                }}
                onDelete={handleDeleteAiRecommendedPosition}
                onGetNewSuggestions={handleRefreshPositions}
                onSaveAll={handleSaveAllRecommendedPositions}
                onPositionClick={handlePositionClick}
                loading={positionsLoading}
                sx={cardFlex1Sx}
              />
            </Box>

            {/* Middle Column */}
            <Box sx={pageColumnWideSx}>
              <CandidateDetailsCard
                sx={cardFlex1Sx}
                memberId={state.candidateInfo?.id}
              />
              <HrCommentCard
                comment={state.candidateInfo?.hrComment || ""}
                onSave={(hrComment) => updateStatement(hrComment)}
              />
            </Box>

            {/* Right Column */}
            <Box sx={pageColumnSx}>
              <QuestionListCard
                questions={state.hrQuestions}
                onQuestionClick={handleQuestionClick}
                sx={cardFlex2Sx}
              />
              <AIQuestionListCard
                hrQuestions={state.aiHrQuestions}
                technicalQuestions={state.aiTechnicalQuestions}
                onHrQuestionClick={handleAiHrQuestionClick}
                onTechnicalQuestionClick={handleAiTechnicalQuestionClick}
                loading={state.loadingAiQuestionList}
                sx={cardFlex3Sx}
              />
            </Box>
          </Box>
        </>
      )}
      <PositionDetailModal
        show={positionDetailModalOpen}
        onClose={() => {
          setPositionDetailModalOpen(false);
          setModalPositionId(null);
        }}
        positionId={modalPositionId}
      />
      <PositionDescriptionModal
        show={positionDescriptionModalOpen}
        onClose={() => setPositionDescriptionModalOpen(false)}
        position={state.positionInfo}
      />
      <AddPositionModal
        show={addPositionModalOpen}
        onAdd={handleAddSelectedPositions}
        onClose={() => setAddPositionModalOpen(false)}
        aiRecommendedPositions={state.aiRecommendedPositions}
      />
      {recordingPopupOpen && currentInterviewId && (
        <ScreenRecorder
          interviewId={currentInterviewId}
          webSocketUrl={WEBSOCKET_URL}
          onClose={() => {
            setRecordingPopupOpen(false);
            setCurrentInterviewId(null);
          }}
        />
      )}
    </PageLayout>
  );
};

export default InterviewAssistant;
